import "./style.css";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import * as OIMO from 'oimo';

/**
 * Base
 */
// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x87ceeb); // Sky blue background

// Add lighting for the 3D model
const ambientLight = new THREE.AmbientLight(0xffffff, 1);
scene.add(ambientLight);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  45, // Reduced field of view to reduce distortion
  sizes.width / sizes.height,
  0.1,
  1000 // Increased far clipping plane for more zoom-out range
);
camera.position.x = 0;
camera.position.y = 35;
camera.position.z = 55;
camera.layers.enable(0); // Default layer for tiles
camera.layers.enable(1); // Enable layer 1 for copper wires
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.target.set(0, 0, 0); // Look at center of the ring

/**
 * 3D Models - Load all tiles in circles
 */
const models = [];

// Outer ring - all tiles
const outerRingTileFiles = [
  'bamboo_one1.glb', 'bamboo_one2.glb', 'bamboo_one3.glb', 'bamboo_one4.glb',
  'bamboo_two1.glb', 'bamboo_two2.glb', 'bamboo_two3.glb', 'bamboo_two4.glb',
  'bamboo_three1.glb', 'bamboo_three2.glb', 'bamboo_three3.glb', 'bamboo_three4.glb',
  'bamboo_four1.glb', 'bamboo_four2.glb', 'bamboo_four3.glb', 'bamboo_four4.glb',
  'bamboo_five1.glb', 'bamboo_five2.glb', 'bamboo_five3.glb', 'bamboo_five4.glb',
  'bamboo_six1.glb', 'bamboo_six2.glb', 'bamboo_six3.glb', 'bamboo_six4.glb',
  'bamboo_seven1.glb', 'bamboo_seven2.glb', 'bamboo_seven3.glb', 'bamboo_seven4.glb',
  'bamboo_eight1.glb', 'bamboo_eight2.glb', 'bamboo_eight3.glb', 'bamboo_eight4.glb',
  'bamboo_nine1.glb', 'bamboo_nine2.glb', 'bamboo_nine3.glb', 'bamboo_nine4.glb',
  'dot_one1.glb', 'dot_one2.glb', 'dot_one3.glb', 'dot_one4.glb',
  'dot_two1.glb', 'dot_two2.glb', 'dot_two3.glb', 'dot_two4.glb',
  'dot_three1.glb', 'dot_three2.glb', 'dot_three3.glb', 'dot_three4.glb',
  'dot_four1.glb', 'dot_four2.glb', 'dot_four3.glb', 'dot_four4.glb',
  'dot_five1.glb', 'dot_five2.glb', 'dot_five3.glb', 'dot_five4.glb',
  'dot_six1.glb', 'dot_six2.glb', 'dot_six3.glb', 'dot_six4.glb',
  'dot_seven1.glb', 'dot_seven2.glb', 'dot_seven3.glb', 'dot_seven4.glb',
  'dot_eight1.glb', 'dot_eight2.glb', 'dot_eight3.glb', 'dot_eight4.glb',
  'dot_nine1.glb', 'dot_nine2.glb', 'dot_nine3.glb', 'dot_nine4.glb',
  'one1.glb', 'one2.glb', 'one3.glb', 'one4.glb',
  'two1.glb', 'two2.glb', 'two3.glb', 'two4.glb',
  'three1.glb', 'three2.glb', 'three3.glb', 'three4.glb',
  'four1.glb', 'four2.glb', 'four3.glb', 'four4.glb',
  'five1.glb', 'five2.glb', 'five3.glb', 'five4.glb',
  'six1.glb', 'six2.glb', 'six3.glb', 'six4.glb',
  'seven1.glb', 'seven2.glb', 'seven3.glb', 'seven4.glb',
  'eight1.glb', 'eight2.glb', 'eight3.glb', 'eight4.glb',
  'nine1.glb', 'nine2.glb', 'nine3.glb', 'nine4.glb',
  'east1.glb', 'east2.glb', 'east3.glb', 'east4.glb',
  'north1.glb', 'north2.glb', 'north3.glb', 'north4.glb',
  'south1.glb', 'south2.glb', 'south3.glb', 'south4.glb',
  'west1.glb', 'west2.glb', 'west3.glb', 'west4.glb'
];

// Middle ring - tiles ending in 1, 2, or 3
const middleRingTileFiles = [
  'bamboo_one1.glb', 'bamboo_one2.glb', 'bamboo_one3.glb',
  'bamboo_two1.glb', 'bamboo_two2.glb', 'bamboo_two3.glb',
  'bamboo_three1.glb', 'bamboo_three2.glb', 'bamboo_three3.glb',
  'bamboo_four1.glb', 'bamboo_four2.glb', 'bamboo_four3.glb',
  'bamboo_five1.glb', 'bamboo_five2.glb', 'bamboo_five3.glb',
  'bamboo_six1.glb', 'bamboo_six2.glb', 'bamboo_six3.glb',
  'bamboo_seven1.glb', 'bamboo_seven2.glb', 'bamboo_seven3.glb',
  'bamboo_eight1.glb', 'bamboo_eight2.glb', 'bamboo_eight3.glb',
  'bamboo_nine1.glb', 'bamboo_nine2.glb', 'bamboo_nine3.glb',
  'dot_one1.glb', 'dot_one2.glb', 'dot_one3.glb',
  'dot_two1.glb', 'dot_two2.glb', 'dot_two3.glb',
  'dot_three1.glb', 'dot_three2.glb', 'dot_three3.glb',
  'dot_four1.glb', 'dot_four2.glb', 'dot_four3.glb',
  'dot_five1.glb', 'dot_five2.glb', 'dot_five3.glb',
  'dot_six1.glb', 'dot_six2.glb', 'dot_six3.glb',
  'dot_seven1.glb', 'dot_seven2.glb', 'dot_seven3.glb',
  'dot_eight1.glb', 'dot_eight2.glb', 'dot_eight3.glb',
  'dot_nine1.glb', 'dot_nine2.glb', 'dot_nine3.glb',
  'one1.glb', 'one2.glb', 'one3.glb',
  'two1.glb', 'two2.glb', 'two3.glb',
  'three1.glb', 'three2.glb', 'three3.glb',
  'four1.glb', 'four2.glb', 'four3.glb',
  'five1.glb', 'five2.glb', 'five3.glb',
  'six1.glb', 'six2.glb', 'six3.glb',
  'seven1.glb', 'seven2.glb', 'seven3.glb',
  'eight1.glb', 'eight2.glb', 'eight3.glb',
  'nine1.glb', 'nine2.glb', 'nine3.glb',
  'east1.glb', 'east2.glb', 'east3.glb',
  'north1.glb', 'north2.glb', 'north3.glb',
  'south1.glb', 'south2.glb', 'south3.glb',
  'west1.glb', 'west2.glb', 'west3.glb'
];

// Inner ring - tiles ending in 1 or 2
const innerRingTileFiles = [
  'bamboo_one1.glb', 'bamboo_one2.glb',
  'bamboo_two1.glb', 'bamboo_two2.glb',
  'bamboo_three1.glb', 'bamboo_three2.glb',
  'bamboo_four1.glb', 'bamboo_four2.glb',
  'bamboo_five1.glb', 'bamboo_five2.glb',
  'bamboo_six1.glb', 'bamboo_six2.glb',
  'bamboo_seven1.glb', 'bamboo_seven2.glb',
  'bamboo_eight1.glb', 'bamboo_eight2.glb',
  'bamboo_nine1.glb', 'bamboo_nine2.glb',
  'dot_one1.glb', 'dot_one2.glb',
  'dot_two1.glb', 'dot_two2.glb',
  'dot_three1.glb', 'dot_three2.glb',
  'dot_four1.glb', 'dot_four2.glb',
  'dot_five1.glb', 'dot_five2.glb',
  'dot_six1.glb', 'dot_six2.glb',
  'dot_seven1.glb', 'dot_seven2.glb',
  'dot_eight1.glb', 'dot_eight2.glb',
  'dot_nine1.glb', 'dot_nine2.glb',
  'one1.glb', 'one2.glb',
  'two1.glb', 'two2.glb',
  'three1.glb', 'three2.glb',
  'four1.glb', 'four2.glb',
  'five1.glb', 'five2.glb',
  'six1.glb', 'six2.glb',
  'seven1.glb', 'seven2.glb',
  'eight1.glb', 'eight2.glb',
  'nine1.glb', 'nine2.glb',
  'east1.glb', 'east2.glb',
  'north1.glb', 'north2.glb',
  'south1.glb', 'south2.glb',
  'west1.glb', 'west2.glb'
];

const outerRadius = 42; // Radius of outer ring
const middleRadius = 31; // Radius of middle ring
const innerRadius = 21; // Radius of inner ring (adjusted for better spacing)

const gltfLoader = new GLTFLoader();

// Function to load a ring of tiles
function loadRing(tileFiles, radius, ringName) {
  tileFiles.forEach((filename, index) => {
    gltfLoader.load(
      `/mahjong/source/ring_tiles/${filename}`,
      (gltf) => {
        // Create a container for the model
        const container = new THREE.Group();
        const model = gltf.scene;

        // Store filename in userData for later reference
        container.userData.filename = filename;

        // Center the model within its local space
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);

        // Add model to container
        container.add(model);

        // Calculate position in circle
        const angle = (index / tileFiles.length) * Math.PI * 2;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;

        container.position.set(x, 0, z);

        // Make container face outward by looking at a point twice as far from center
        const lookAtX = x * 2;
        const lookAtZ = z * 2;
        container.lookAt(lookAtX, 0, lookAtZ);

        scene.add(container);
        models.push(container);

        // Update counter after tiles are loaded
        updateCounter();
      },
      undefined,
      (error) => {
        console.error(`Error loading ${ringName} ${filename}:`, error);
      }
    );
  });
}

// Function to get tile base name (without number suffix)
function getTileBaseName(filename) {
  // Remove the number and .glb extension
  return filename.replace(/\d+\.glb$/, '');
}

// Function to shuffle array ensuring no adjacent tiles are the same type
function shuffleWithNoAdjacent(array) {
  const shuffled = [...array];

  // Fisher-Yates shuffle
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  // Check and fix adjacent same tiles
  let maxAttempts = 100;
  let attempts = 0;

  while (attempts < maxAttempts) {
    let hasAdjacent = false;

    for (let i = 0; i < shuffled.length; i++) {
      const current = getTileBaseName(shuffled[i]);
      const next = getTileBaseName(shuffled[(i + 1) % shuffled.length]);

      if (current === next) {
        hasAdjacent = true;

        // Find a position to swap with that won't create new adjacencies
        let swapIndex = -1;
        for (let j = 0; j < shuffled.length; j++) {
          if (j === i || j === (i + 1) % shuffled.length) continue;

          const prev = getTileBaseName(shuffled[(j - 1 + shuffled.length) % shuffled.length]);
          const nextJ = getTileBaseName(shuffled[(j + 1) % shuffled.length]);
          const currentBase = getTileBaseName(shuffled[i]);

          if (prev !== currentBase && nextJ !== currentBase) {
            swapIndex = j;
            break;
          }
        }

        if (swapIndex !== -1) {
          [shuffled[i], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[i]];
        }
      }
    }

    if (!hasAdjacent) break;
    attempts++;
  }

  return shuffled;
}

// Shuffle the tile arrays
const shuffledOuterRing = shuffleWithNoAdjacent(outerRingTileFiles);
const shuffledMiddleRing = shuffleWithNoAdjacent(middleRingTileFiles);
const shuffledInnerRing = shuffleWithNoAdjacent(innerRingTileFiles);

// Load all three rings with shuffled tiles
loadRing(shuffledOuterRing, outerRadius, 'outer ring');
loadRing(shuffledMiddleRing, middleRadius, 'middle ring');
loadRing(shuffledInnerRing, innerRadius, 'inner ring');

/**
 * Player Character - Green Sphere
 */
const playerGeometry = new THREE.SphereGeometry(0.5, 32, 32); // 1/3 of original size (1.5 -> 0.5)
const playerMaterial = new THREE.MeshStandardMaterial({
  color: 0x1a6e3a, // Dark green
  emissive: 0x000000, // No emissive glow
  emissiveIntensity: 0
});
const player = new THREE.Mesh(playerGeometry, playerMaterial);
player.position.set(0, 0.5, outerRadius + 5); // Position outside outer ring, adjusted y for smaller size
player.renderOrder = 999; // Ensure sphere renders on top
scene.add(player);

/**
 * Transistor Teardrops - Between outer and middle rings
 */
const transistorMaterial = new THREE.MeshStandardMaterial({
  color: 0x2b6f9e, // Darker sky blue/cyan
  metalness: 0.3,
  roughness: 0.4,
  emissive: 0x2b6f9e, // Emissive darker sky blue/cyan for glow
  emissiveIntensity: 0.1
});

// Helper function to create a teardrop
function createTeardrop() {
  const teardropGroup = new THREE.Group();

  // Bottom sphere (rounded bottom of teardrop)
  const sphereGeometry = new THREE.SphereGeometry(0.3, 16, 16);
  const sphere = new THREE.Mesh(sphereGeometry, transistorMaterial.clone());
  sphere.position.y = 0;
  teardropGroup.add(sphere);

  // Top cone (pointed top of teardrop)
  const coneGeometry = new THREE.ConeGeometry(0.3, 0.6, 16);
  const cone = new THREE.Mesh(coneGeometry, transistorMaterial.clone());
  cone.position.y = 0.3;
  teardropGroup.add(cone);

  // Mark as teardrop for identification
  teardropGroup.userData.isTeardrop = true;

  return teardropGroup;
}

// Array to store all teardrops (for click detection)
const teardrops = [];

// Define radii for teardrop locations (matching copper ore pattern)
const teardropRadii = [
  (outerRadius + middleRadius) / 2, // Between outer and middle rings
  (middleRadius + innerRadius) / 2, // Between middle and inner rings
  innerRadius / 2 // Inside inner ring
];

// Generate random positions for each teardrop
const teardropPositions = teardropRadii.map(radius => {
  const randomAngle = Math.random() * Math.PI * 2; // Random angle 0 to 360 degrees
  return {
    x: radius * Math.cos(randomAngle),
    z: radius * Math.sin(randomAngle),
    y: 0.5,
    radius: radius
  };
});

// Create teardrops at each position
teardropPositions.forEach((position, index) => {
  const teardrop = createTeardrop();
  teardrop.position.set(position.x, position.y, position.z);
  scene.add(teardrop);
  teardrops.push(teardrop);
  console.log(`Teardrop ${index + 1} placed at position:`, position);
});

// Test teardrops removed

// Store reference for animation (use the first teardrop for backward compatibility)
const transistorPyramid = teardrops[0];

// SEED OF LIFE: Will be created after player arranges 7 teardrops
const seedOfLifeCenter = { x: 0, y: 0.5, z: 0 }; // At the center of the rings
const seedOfLifeCircleRadius = 9; // Radius of each circle in the pattern (3x bigger)
const seedOfLifeTeardrops = [];
const seedOfLifeCircles = [];
let seedOfLifeCreated = false; // Track if Seed of Life has been created

// Helper function to create a circle outline with maze-like fractal pattern
function createCircleOutline(radius, color = 0xc87533, position = { x: 0, y: 0.5, z: 0 }) {
  const segments = 32; // Number of segments around the circle
  const points = [];

  // Generate points around a circle
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const x = position.x + Math.cos(angle) * radius;
    const z = position.z + Math.sin(angle) * radius;
    points.push(new THREE.Vector3(x, position.y, z));
  }

  // Now create fractal paths between consecutive points
  const fractalPoints = [];
  for (let i = 0; i < points.length - 1; i++) {
    const startPoint = points[i];
    const endPoint = points[i + 1];

    // Generate maze-like path between these two points
    const segmentPath = generateFractalPath(startPoint, endPoint, 3); // Depth 3 for circle segments

    // Add all points except the last one (to avoid duplicates)
    for (let j = 0; j < segmentPath.length - 1; j++) {
      fractalPoints.push(segmentPath[j]);
    }
  }
  // Add the final point to close the circle
  fractalPoints.push(points[points.length - 1]);

  // Create thick 3D copper tube for the circle
  const curve = new THREE.CatmullRomCurve3(fractalPoints);
  const tubeGeometry = new THREE.TubeGeometry(curve, fractalPoints.length * 2, 0.08, 16, false);
  const tubeMaterial = new THREE.MeshStandardMaterial({
    color: color,
    metalness: 0.8,
    roughness: 0.3,
    emissive: 0xd96a2e,
    emissiveIntensity: 0.5
  });

  const circle = new THREE.Mesh(tubeGeometry, tubeMaterial);
  return circle;
}

// Function to create the Seed of Life structure
function createSeedOfLife() {
  if (seedOfLifeCreated) return; // Already created

  seedOfLifeCreated = true;

  // Create center teardrop
  const centerSeedTeardrop = createTeardrop();
  centerSeedTeardrop.position.set(seedOfLifeCenter.x, seedOfLifeCenter.y, seedOfLifeCenter.z);
  scene.add(centerSeedTeardrop);
  teardrops.push(centerSeedTeardrop);
  seedOfLifeTeardrops.push(centerSeedTeardrop);

  // Create circle around center teardrop
  const centerCircle = createCircleOutline(seedOfLifeCircleRadius, 0xc87533, seedOfLifeCenter);
  scene.add(centerCircle);
  seedOfLifeCircles.push(centerCircle);
  console.log('Seed of Life center teardrop and circle created at:', seedOfLifeCenter);

  // Create 6 surrounding teardrops and circles in hexagonal pattern
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI) / 3; // 60 degrees apart
    const position = {
      x: seedOfLifeCenter.x + seedOfLifeCircleRadius * Math.cos(angle),
      y: seedOfLifeCenter.y,
      z: seedOfLifeCenter.z + seedOfLifeCircleRadius * Math.sin(angle)
    };

    // Create teardrop
    const teardrop = createTeardrop();
    teardrop.position.set(position.x, position.y, position.z);
    scene.add(teardrop);
    teardrops.push(teardrop);
    seedOfLifeTeardrops.push(teardrop);

    // Create circle around teardrop
    const circle = createCircleOutline(seedOfLifeCircleRadius, 0xc87533, position);
    scene.add(circle);
    seedOfLifeCircles.push(circle);

    console.log(`Seed of Life teardrop ${i + 1} and circle created at:`, position);
  }

  console.log('✨ SEED OF LIFE CREATED! ✨');
}

// Function to check if a teardrop is the center of a valid Seed of Life pattern
function checkSeedOfLifePattern(centralTeardrop) {
  // Check if the central teardrop is in the innermost area (inside inner ring)
  const centralDist = Math.sqrt(centralTeardrop.position.x ** 2 + centralTeardrop.position.z ** 2);
  if (centralDist > innerRadius) {
    return null; // Not in innermost area
  }

  // Find all teardrops within reasonable distance that could be the 6 surrounding ones
  // First, let's find ALL teardrops with their distances for debugging
  const allDistances = teardrops
    .filter(td => td !== centralTeardrop)
    .map(td => {
      const dx = td.position.x - centralTeardrop.position.x;
      const dz = td.position.z - centralTeardrop.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      return { teardrop: td, distance: dist };
    })
    .sort((a, b) => a.distance - b.distance);

  console.log('All teardrop distances from clicked teardrop:', allDistances.map(d => d.distance.toFixed(2)));

  // Take the 6 closest teardrops (excluding the center itself)
  const surroundingTeardrops = allDistances.slice(0, 6).map(d => d.teardrop);

  // Need at least 6 teardrops total in the scene
  if (surroundingTeardrops.length < 6) {
    console.log(`Only ${surroundingTeardrops.length} other teardrops exist, need at least 6`);
    return null;
  }

  // Count how many wires are connected to this teardrop using userData
  // The true center should have 6 wires, outer ones should have only 1
  let wiresConnectedToCentral = 0;

  console.log(`Checking ${completedWires.length} completed wires...`);

  for (const wire of completedWires) {
    // Check if this wire has teardrop connection metadata
    if (wire.userData && (wire.userData.startTeardrop === centralTeardrop || wire.userData.endTeardrop === centralTeardrop)) {
      wiresConnectedToCentral++;
      console.log(`  Wire connected to clicked teardrop!`);
    }
  }

  console.log(`Total wires connected to clicked teardrop: ${wiresConnectedToCentral}`);

  // The central teardrop must have exactly 6 wires connected to it
  if (wiresConnectedToCentral !== 6) {
    console.log(`Clicked teardrop has ${wiresConnectedToCentral} wires, need 6 to be the center`);
    return null;
  }

  // Check if all 6 surrounding teardrops are connected to the central teardrop via copper wires
  let connectedCount = 0;
  const wiresFromCenter = [];

  // Check each completed wire using userData to see if it connects central teardrop to a surrounding one
  for (const wire of completedWires) {
    // Skip wires without teardrop metadata
    if (!wire.userData || (!wire.userData.startTeardrop && !wire.userData.endTeardrop)) {
      continue;
    }

    // Check if this wire connects the central teardrop to any of the surrounding teardrops
    const connectsToCentral = (wire.userData.startTeardrop === centralTeardrop || wire.userData.endTeardrop === centralTeardrop);

    if (connectsToCentral) {
      // Find which teardrop is on the other end
      const otherTeardrop = wire.userData.startTeardrop === centralTeardrop ? wire.userData.endTeardrop : wire.userData.startTeardrop;

      // Check if the other end connects to one of the surrounding teardrops
      if (surroundingTeardrops.includes(otherTeardrop)) {
        connectedCount++;
        wiresFromCenter.push(wire);
      }
    }
  }

  console.log(`Found ${connectedCount} wires connecting center to surrounding teardrops`);

  // All 6 must be connected
  if (connectedCount !== 6) {
    console.log(`Only ${connectedCount} surrounding teardrops connected to center, need 6`);
    return null;
  }

  console.log('✅ Valid Seed of Life pattern detected!');
  return {
    centralTeardrop,
    surroundingTeardrops,
    wires: wiresFromCenter
  };
}

// Function to activate Seed of Life by replacing player's arrangement
function activateSeedOfLife(seedOfLifeData) {
  console.log('🌟 ACTIVATING SEED OF LIFE! 🌟');

  // Update text to final message
  updateTextWithFade(`you have arrived, and the sacred circuitry is unlocked!`);

  // Collect all objects to dissolve
  const objectsToDissolve = [];

  // Add teardrops to dissolution list
  objectsToDissolve.push(seedOfLifeData.centralTeardrop);
  objectsToDissolve.push(...seedOfLifeData.surroundingTeardrops);

  // Add wires to dissolution list
  objectsToDissolve.push(...seedOfLifeData.wires);

  // Find copper ore inside inner ring (radius < innerRadius)
  const innerCopperOre = copperOres.find(copper => {
    const dist = Math.sqrt(copper.position.x ** 2 + copper.position.z ** 2);
    return dist < innerRadius;
  });

  if (innerCopperOre) {
    objectsToDissolve.push(innerCopperOre);
    console.log('Found copper ore inside inner ring to dissolve');
  }

  // Animate dissolution over 2 seconds
  const dissolutionDuration = 2000; // 2 seconds
  const startTime = Date.now();

  const dissolveAnimation = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / dissolutionDuration, 1);

    // Fade out opacity and scale down
    const opacity = 1 - progress;
    const scale = 1 - (progress * 0.5); // Shrink to 50% then disappear

    objectsToDissolve.forEach(obj => {
      if (!obj || !obj.parent) return; // Skip if already removed

      // Update scale
      obj.scale.multiplyScalar(scale / obj.scale.x); // Normalize then apply new scale

      // Update opacity for all materials
      obj.traverse((child) => {
        if (child.isMesh && child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              mat.transparent = true;
              mat.opacity = opacity;
            });
          } else {
            child.material.transparent = true;
            child.material.opacity = opacity;
          }
        }
      });
    });

    if (progress < 1) {
      requestAnimationFrame(dissolveAnimation);
    } else {
      // Dissolution complete - remove objects
      scene.remove(seedOfLifeData.centralTeardrop);
      const centralIndex = teardrops.indexOf(seedOfLifeData.centralTeardrop);
      if (centralIndex > -1) teardrops.splice(centralIndex, 1);

      for (const teardrop of seedOfLifeData.surroundingTeardrops) {
        scene.remove(teardrop);
        const index = teardrops.indexOf(teardrop);
        if (index > -1) teardrops.splice(index, 1);
      }

      for (const wire of seedOfLifeData.wires) {
        scene.remove(wire);
        const index = completedWires.indexOf(wire);
        if (index > -1) completedWires.splice(index, 1);
      }

      if (innerCopperOre) {
        scene.remove(innerCopperOre);
        const copperIndex = copperOres.indexOf(innerCopperOre);
        if (copperIndex > -1) copperOres.splice(copperIndex, 1);
      }

      console.log('Dissolution complete! Creating Seed of Life...');

      // Wait 500ms then create Seed of Life with fade-in animation
      setTimeout(() => {
        createSeedOfLifeWithFadeIn();
      }, 500);
    }
  };

  dissolveAnimation();
}

// Create Seed of Life with fade-in animation
function createSeedOfLifeWithFadeIn() {
  // Remember the current lengths to know which objects are new
  const initialTeardropCount = seedOfLifeTeardrops.length;
  const initialCircleCount = seedOfLifeCircles.length;

  // Create the Seed of Life structure
  createSeedOfLife();

  // Get the newly created objects
  const newTeardrops = seedOfLifeTeardrops.slice(initialTeardropCount);
  const newCircles = seedOfLifeCircles.slice(initialCircleCount);
  const allNewObjects = [...newTeardrops, ...newCircles];

  console.log(`Fading in ${newTeardrops.length} teardrops and ${newCircles.length} circles`);

  // Make all new objects initially invisible
  allNewObjects.forEach(obj => {
    obj.traverse((child) => {
      if (child.isMesh && child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(mat => {
            mat.transparent = true;
            mat.opacity = 0;
          });
        } else {
          child.material.transparent = true;
          child.material.opacity = 0;
        }
      }
    });
  });

  // Fade in over 2 seconds
  const fadeInDuration = 2000;
  const startTime = Date.now();

  const fadeInAnimation = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / fadeInDuration, 1);

    allNewObjects.forEach(obj => {
      obj.traverse((child) => {
        if (child.isMesh && child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              mat.opacity = progress;
            });
          } else {
            child.material.opacity = progress;
          }
        }
      });
    });

    if (progress < 1) {
      requestAnimationFrame(fadeInAnimation);
    } else {
      console.log('✨ SEED OF LIFE FADE-IN COMPLETE! ✨');
    }
  };

  fadeInAnimation();
}

// Grabbed teardrop tracking
let grabbedTeardrop = null;
let movingTeardrop = null; // Track teardrop being moved with arrow keys

/**
 * Native Copper - Add in three locations with random angles
 */
// Array to store copper ore objects
const copperOres = [];

// AND Gate - Simple shape between outer and middle rings
// We'll create this after generateFractalPath is defined
const andGateWires = [];

// Define radii for three locations
const copperRadii = [
  (outerRadius + middleRadius) / 2, // Between outer and middle rings
  (middleRadius + innerRadius) / 2, // Between middle and inner rings
  innerRadius / 2 // Inside inner ring
];

// Generate random positions for each copper ore
const nativeCopperPositions = copperRadii.map(radius => {
  const randomAngle = Math.random() * Math.PI * 2; // Random angle 0 to 360 degrees
  return {
    x: radius * Math.cos(randomAngle),
    z: radius * Math.sin(randomAngle),
    y: 0.5
  };
});

nativeCopperPositions.forEach((position, index) => {
  gltfLoader.load(
    '/mahjong/source/native_copper.glb',
    (gltf) => {
      const nativeCopper = gltf.scene;
      nativeCopper.position.set(position.x, position.y, position.z);

      // Scale the native copper to appropriate size (25% of original)
      nativeCopper.scale.set(0.25, 0.25, 0.25);

      // Mark as copper ore for identification
      nativeCopper.userData.isCopperOre = true;

      // Enhance materials without overriding original colors/textures
      nativeCopper.traverse((child) => {
        if (child.isMesh && child.material) {
          // Clone material to avoid affecting other instances
          child.material = child.material.clone();

          // Brighten the existing color without changing it
          if (child.material.color) {
            child.material.color.multiplyScalar(1.8); // Make existing color brighter
          }

          // Add subtle emissive glow without washing out the color
          if (child.material.color && child.material.emissive) {
            child.material.emissive.copy(child.material.color).multiplyScalar(0.2);
            child.material.emissiveIntensity = 0.4;
          }

          // Force the material to update
          child.material.needsUpdate = true;
        }
      });

      scene.add(nativeCopper);
      copperOres.push(nativeCopper);
      console.log(`Native copper ${index + 1} loaded at position:`, position);
    },
    undefined,
    (error) => {
      console.error(`Error loading native copper ${index + 1}:`, error);
    }
  );
});

// Test copper ore removed

// Player movement
const playerSpeed = 0.1;
const keys = {
  left: false,
  right: false,
  up: false,
  down: false
};

// Player copper mode state
let playerHasCopper = false; // Start without copper
const originalPlayerColor = 0x1a6e3a; // Dark green
const copperColor = 0xb87333; // Copper color

// Wire drawing state
const wirePoints = []; // Array to store wire line points
let currentWireLine = null; // Currently drawing wire line
let currentWireMesh = null; // Currently drawing wire mesh (for thick lines)
let isDrawingWire = false; // Track if currently in drawing mode
let wireStartTeardrop = null; // Track the teardrop where wire drawing started
const completedWires = []; // Array to store all completed wire meshes

// Generate maze-like path with right-angle turns
function generateFractalPath(startPoint, endPoint, depth = 5) {
  const points = [];

  // Start at the starting point
  let currentX = startPoint.x;
  let currentZ = startPoint.z;
  const currentY = startPoint.y;

  points.push(new THREE.Vector3(currentX, currentY, currentZ));

  // Calculate total distance to cover
  const totalDx = endPoint.x - startPoint.x;
  const totalDz = endPoint.z - startPoint.z;
  const totalDistance = Math.sqrt(totalDx * totalDx + totalDz * totalDz);

  // Number of turns to create
  const numTurns = Math.pow(2, depth); // 2^depth turns
  const stepSize = totalDistance / numTurns;

  // Calculate perpendicular directions (for the "maze" turns)
  const forwardX = totalDx / totalDistance;
  const forwardZ = totalDz / totalDistance;
  const perpX = -forwardZ; // 90 degree rotation
  const perpZ = forwardX;

  // Create zigzag path with right angles
  for (let i = 0; i < numTurns; i++) {
    // First move perpendicular (left or right)
    const perpDistance = stepSize * 2.0; // How far to move perpendicular (increased for bigger maze effect)
    const direction = (i % 2 === 0) ? 1 : -1; // Alternate left/right

    currentX += perpX * perpDistance * direction;
    currentZ += perpZ * perpDistance * direction;
    points.push(new THREE.Vector3(currentX, currentY, currentZ));

    // Then move forward toward target
    currentX += forwardX * stepSize;
    currentZ += forwardZ * stepSize;
    points.push(new THREE.Vector3(currentX, currentY, currentZ));
  }

  // Ensure we end exactly at the endpoint
  points[points.length - 1] = new THREE.Vector3(endPoint.x, currentY, endPoint.z);

  return points;
}

// Helper function to generate fractal wire path between two 3D points
function generateHilbertPath(startPoint, endPoint, order = 5) {
  // Use recursive subdivision instead of Hilbert curve
  return generateFractalPath(startPoint, endPoint, order);
}

// Helper function to create a thin Hilbert curve line
function createHilbertLine(points) {
  if (points.length < 2) return null;

  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineBasicMaterial({
    color: 0xc87533, // Darker rust/copper color
    linewidth: 2
  });

  const line = new THREE.Line(geometry, material);
  return line;
}

// Helper function to create a thick line using TubeGeometry (kept for compatibility)
function createThickLine(points, radius = 0.1) {
  if (points.length < 2) return null;

  const curve = new THREE.CatmullRomCurve3(points);
  const tubeGeometry = new THREE.TubeGeometry(curve, points.length * 2, radius, 16, false);
  const tubeMaterial = new THREE.MeshStandardMaterial({
    color: 0xc87533, // Darker rust/copper color
    metalness: 0.8,
    roughness: 0.3,
    emissive: 0xd96a2e,
    emissiveIntensity: 0.5
  });

  const wireMesh = new THREE.Mesh(tubeGeometry, tubeMaterial);

  return wireMesh;
}

// Tile grabbing system
let grabbedTile = null;
const grabDistance = 3; // Distance at which player can grab a tile

// Counter for matching pairs in outer ring
let outerRingPairs = 0;
const counterElement = document.getElementById('counter');
let playerPassedOuterRing = false; // Track if player has passed through outer ring
let playerPassedMiddleRing = false; // Track if player has passed through middle ring
let playerPassedInnerRing = false; // Track if player has passed through inner ring
let outerDoorTiles = null; // Store the outer ring door tiles
let outerDoorsOpen = false;
let outerDoorGapAngle = null; // Store the angle where the outer door gap is located
let middleDoorTiles = null; // Store the middle ring door tiles
let middleDoorsOpen = false;
let middleDoorGapAngle = null; // Store the angle where the middle door gap is located
let innerDoorTiles = null; // Store the inner ring door tiles
let innerDoorsOpen = false;
let innerDoorGapAngle = null; // Store the angle where the inner door gap is located
let playerLastRadius = outerRadius + 5; // Track player's previous radius to detect ring transitions
let ignoreTilesForCollision = new Set(); // Tiles to ignore for collision temporarily

// Function to get filename from a tile's loaded model
function getTileFilename(tile) {
  // Store filename on tile when loading
  return tile.userData.filename || '';
}

// Function to count matching pairs in outer ring
function countOuterRingPairs() {
  // Get all tiles in the outer ring
  const outerRingTiles = models.filter(tile => {
    const distFromCenter = Math.sqrt(tile.position.x ** 2 + tile.position.z ** 2);
    return Math.abs(distFromCenter - outerRadius) < 5;
  });

  if (outerRingTiles.length === 0) return 0;

  // Sort tiles by angle to get them in circular order
  const tilesWithAngles = outerRingTiles.map(tile => ({
    tile: tile,
    angle: Math.atan2(tile.position.z, tile.position.x)
  }));
  tilesWithAngles.sort((a, b) => a.angle - b.angle);

  // Count adjacent matching pairs
  let pairs = 0;

  for (let i = 0; i < tilesWithAngles.length; i++) {
    const current = getTileBaseName(getTileFilename(tilesWithAngles[i].tile));
    const next = getTileBaseName(getTileFilename(tilesWithAngles[(i + 1) % tilesWithAngles.length].tile));

    if (current && next && current === next) {
      pairs++;
    }
  }

  // Each pair is counted twice (once for each tile), so divide by 2
  return Math.floor(pairs / 2);
}

// Function to open the door tiles inward
function openDoors(tile1, tile2, ringRadius) {
  console.log('Opening doors!', tile1.userData.filename, tile2.userData.filename, 'Ring radius:', ringRadius);

  // Calculate angles before removing tiles
  const tile1Angle = Math.atan2(tile1.position.z, tile1.position.x);
  const tile2Angle = Math.atan2(tile2.position.z, tile2.position.x);

  // Calculate average angle (midpoint between the two tiles) - this is where the gap will be
  let avgAngle = (tile1Angle + tile2Angle) / 2;

  // Handle wraparound case
  if (Math.abs(tile1Angle - tile2Angle) > Math.PI) {
    avgAngle = (tile1Angle + tile2Angle) / 2 + Math.PI;
  }

  // Store the door gap angle, tiles, and original positions based on which ring this is
  if (ringRadius === outerRadius) {
    outerDoorsOpen = true;
    outerDoorTiles = {
      tile1,
      tile2,
      tile1OriginalAngle: tile1Angle,
      tile2OriginalAngle: tile2Angle
    };
    outerDoorGapAngle = avgAngle;
  } else if (ringRadius === middleRadius) {
    middleDoorsOpen = true;
    middleDoorTiles = {
      tile1,
      tile2,
      tile1OriginalAngle: tile1Angle,
      tile2OriginalAngle: tile2Angle
    };
    middleDoorGapAngle = avgAngle;
  } else if (ringRadius === innerRadius) {
    innerDoorsOpen = true;
    innerDoorTiles = {
      tile1,
      tile2,
      tile1OriginalAngle: tile1Angle,
      tile2OriginalAngle: tile2Angle
    };
    innerDoorGapAngle = avgAngle;
  }

  // Remove door tiles from models array so they don't collide with player
  const index1 = models.indexOf(tile1);
  if (index1 > -1) {
    models.splice(index1, 1);
  }
  const index2 = models.indexOf(tile2);
  if (index2 > -1) {
    models.splice(index2, 1);
  }

  const backwardDistance = 3; // How far backward (toward center) to move

  // Calculate how far apart the tiles should slide based on the ring gap
  const backwardRadius = ringRadius - backwardDistance;

  // Count remaining tiles in this ring (after removing door tiles)
  const remainingTiles = models.filter(tile => {
    const tileDist = Math.sqrt(tile.position.x ** 2 + tile.position.z ** 2);
    return Math.abs(tileDist - ringRadius) < 5;
  }).length;

  // Total positions including the gap for 2 tiles
  const totalPositions = remainingTiles + 2;

  // Angular gap for 2 tile positions at the RING radius (not backward radius)
  const gapAngle = (2 / totalPositions) * Math.PI * 2;

  // Each tile position has an angular width
  const tileAngularWidth = (1 / totalPositions) * Math.PI * 2;

  // Calculate the ring gap edges in angular terms
  // The gap spans 2 tile positions, so it covers gapAngle radians
  // The gap is centered at avgAngle
  // Push tiles outward by half a tile width so their inner edges (not centers) align with gap edges
  const gapStartAngle = avgAngle - gapAngle / 2 - tileAngularWidth / 2;
  const gapEndAngle = avgAngle + gapAngle / 2 + tileAngularWidth / 2;

  // Store original positions
  const tile1StartX = tile1.position.x;
  const tile1StartZ = tile1.position.z;
  const tile2StartX = tile2.position.x;
  const tile2StartZ = tile2.position.z;

  // Phase 1: Move backward (toward center) - use the ring's specific radius
  const tile1BackX = Math.cos(tile1Angle) * backwardRadius;
  const tile1BackZ = Math.sin(tile1Angle) * backwardRadius;
  const tile2BackX = Math.cos(tile2Angle) * backwardRadius;
  const tile2BackZ = Math.sin(tile2Angle) * backwardRadius;

  // Phase 2: Position tiles so their inner edges align with the gap edges at the backward radius
  // Convert the gap edge angles to positions at the backward radius
  const tile1FinalX = Math.cos(gapStartAngle) * backwardRadius;
  const tile1FinalZ = Math.sin(gapStartAngle) * backwardRadius;
  const tile2FinalX = Math.cos(gapEndAngle) * backwardRadius;
  const tile2FinalZ = Math.sin(gapEndAngle) * backwardRadius;

  const phaseDuration = 500; // milliseconds per phase
  const startTime = Date.now();

  // Create a unique animation ID for this door opening animation
  const animationId = Date.now() + Math.random();

  // Store animation IDs on the tiles so they can be cancelled
  tile1.userData.doorAnimationId = animationId;
  tile2.userData.doorAnimationId = animationId;

  function animate() {
    // Check if this animation should be cancelled (when closeDoors is called)
    if (tile1.userData.doorAnimationId !== animationId || tile2.userData.doorAnimationId !== animationId) {
      return; // Stop this animation
    }

    const elapsed = Date.now() - startTime;
    const totalDuration = phaseDuration * 2;

    if (elapsed < phaseDuration) {
      // Phase 1: Move backward
      const progress = elapsed / phaseDuration;
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      tile1.position.x = tile1StartX + (tile1BackX - tile1StartX) * easeProgress;
      tile1.position.z = tile1StartZ + (tile1BackZ - tile1StartZ) * easeProgress;
      tile2.position.x = tile2StartX + (tile2BackX - tile2StartX) * easeProgress;
      tile2.position.z = tile2StartZ + (tile2BackZ - tile2StartZ) * easeProgress;
    } else {
      // Phase 2: Slide apart
      const progress = (elapsed - phaseDuration) / phaseDuration;
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      tile1.position.x = tile1BackX + (tile1FinalX - tile1BackX) * easeProgress;
      tile1.position.z = tile1BackZ + (tile1FinalZ - tile1BackZ) * easeProgress;
      tile2.position.x = tile2BackX + (tile2FinalX - tile2BackX) * easeProgress;
      tile2.position.z = tile2BackZ + (tile2FinalZ - tile2BackZ) * easeProgress;
    }

    // Keep tiles facing outward
    const lookAtX1 = tile1.position.x * 2;
    const lookAtZ1 = tile1.position.z * 2;
    tile1.lookAt(lookAtX1, 0, lookAtZ1);

    const lookAtX2 = tile2.position.x * 2;
    const lookAtZ2 = tile2.position.z * 2;
    tile2.lookAt(lookAtX2, 0, lookAtZ2);

    if (elapsed < totalDuration) {
      requestAnimationFrame(animate);
    } else {
      // Animation complete, clear the animation IDs
      tile1.userData.doorAnimationId = null;
      tile2.userData.doorAnimationId = null;
    }
  }

  animate();
}

// Function to close the doors and return them to the ring
function closeDoors(ringRadius) {
  console.log('Closing doors for ring radius:', ringRadius);

  let doorTiles;

  if (ringRadius === outerRadius) {
    if (!outerDoorsOpen || !outerDoorTiles) return;
    doorTiles = outerDoorTiles;
  } else if (ringRadius === middleRadius) {
    if (!middleDoorsOpen || !middleDoorTiles) return;
    doorTiles = middleDoorTiles;
  } else if (ringRadius === innerRadius) {
    if (!innerDoorsOpen || !innerDoorTiles) return;
    doorTiles = innerDoorTiles;
  } else {
    return;
  }

  const { tile1, tile2 } = doorTiles;

  // Reset door state FIRST
  if (ringRadius === outerRadius) {
    outerDoorsOpen = false;
    outerDoorTiles = null;
    outerDoorGapAngle = null;
  } else if (ringRadius === middleRadius) {
    middleDoorsOpen = false;
    middleDoorTiles = null;
    middleDoorGapAngle = null;
  } else if (ringRadius === innerRadius) {
    innerDoorsOpen = false;
    innerDoorTiles = null;
    innerDoorGapAngle = null;
  }

  // Mark these tiles to ignore collision temporarily
  ignoreTilesForCollision.add(tile1);
  ignoreTilesForCollision.add(tile2);

  // Simply add tiles back to models array
  models.push(tile1);
  models.push(tile2);

  // Rearrange all rings to evenly distribute tiles (including the returned door tiles)
  rearrangeAllRings();

  // After a short delay, re-enable collision for these tiles
  setTimeout(() => {
    ignoreTilesForCollision.delete(tile1);
    ignoreTilesForCollision.delete(tile2);
  }, 1000); // 1 second should be enough for player to move away
}

// Function to update text with fade effect
function updateTextWithFade(newText) {
  // Fade out current text
  counterElement.style.transition = 'opacity 1s';
  counterElement.style.opacity = '0';

  // After fade out completes, change text and fade in
  setTimeout(() => {
    counterElement.textContent = newText;
    counterElement.style.opacity = '1';
  }, 1000);
}

// Function to update counter display
function updateCounter(checkDoors = false) {
  outerRingPairs = countOuterRingPairs();

  // Only show welcome message if player hasn't progressed through any rings yet
  if (!playerPassedOuterRing && !playerPassedMiddleRing && !playerPassedInnerRing) {
    counterElement.textContent = `welcome to mahjong circuitry. you are the sphere. find a pair of matching mahjong tiles in the outer ring by using the arrow keys to move around. click on a tile to grab it and move it to where its matching pair is. once you put them together, you will be able to enter the ring to the next level.`;
  }

  console.log('updateCounter called, checkDoors:', checkDoors, 'pairs:', outerRingPairs, 'outerDoorsOpen:', outerDoorsOpen);

  // Only check for doors if explicitly requested (i.e., after player places a tile)
  if (checkDoors && outerRingPairs >= 1 && !outerDoorsOpen) {
    console.log('Checking for door pairs...');
    // Find the first matching pair
    const outerRingTiles = models.filter(tile => {
      const distFromCenter = Math.sqrt(tile.position.x ** 2 + tile.position.z ** 2);
      return Math.abs(distFromCenter - outerRadius) < 5;
    });

    console.log('Outer ring tiles found:', outerRingTiles.length);
    if (outerRingTiles.length === 0) return;

    const tilesWithAngles = outerRingTiles.map(tile => ({
      tile: tile,
      angle: Math.atan2(tile.position.z, tile.position.x)
    }));
    tilesWithAngles.sort((a, b) => a.angle - b.angle);

    for (let i = 0; i < tilesWithAngles.length; i++) {
      const current = getTileBaseName(getTileFilename(tilesWithAngles[i].tile));
      const next = getTileBaseName(getTileFilename(tilesWithAngles[(i + 1) % tilesWithAngles.length].tile));

      console.log(`Checking tiles ${i}: ${tilesWithAngles[i].tile.userData.filename} (${current}) vs ${tilesWithAngles[(i + 1) % tilesWithAngles.length].tile.userData.filename} (${next})`);

      if (current && next && current === next) {
        console.log('Found matching pair! Opening doors...');
        // alert('Opening doors for: ' + current); // Removed popup
        openDoors(tilesWithAngles[i].tile, tilesWithAngles[(i + 1) % tilesWithAngles.length].tile, outerRadius);
        break;
      }
    }
  }
}

window.addEventListener('keydown', (event) => {
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    keys.left = true;
  }
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    keys.right = true;
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault();
    keys.up = true;
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    keys.down = true;
  }
});

window.addEventListener('keyup', (event) => {
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    keys.left = false;
  }
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    keys.right = false;
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault();
    keys.up = false;
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    keys.down = false;
  }

  // Delete key to remove the most recently drawn wire
  if (event.key === 'Delete' || event.key === 'Backspace') {
    if (playerHasCopper && completedWires.length > 0) {
      // Remove the most recently completed wire
      const lastWire = completedWires.pop();
      scene.remove(lastWire);
      console.log('Deleted most recent wire. Remaining wires:', completedWires.length);
    }
  }
});

// Mouse click handling for tile grabbing
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

window.addEventListener('click', (event) => {
  // Calculate mouse position in normalized device coordinates
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

  // Update the raycaster with the camera and mouse position
  raycaster.setFromCamera(mouse, camera);

  // Check if clicking on the player sphere (for wire drawing toggle)
  const playerIntersects = raycaster.intersectObject(player, false);

  if (playerIntersects.length > 0 && playerHasCopper) {
    // Toggle wire drawing mode
    toggleWireDrawing();
    return;
  }

  // If holding a teardrop, check if clicking on it to place it down
  if (grabbedTeardrop) {
    const intersectsGrabbedTeardrop = raycaster.intersectObjects([grabbedTeardrop], true);

    if (intersectsGrabbedTeardrop.length > 0) {
      // Clicked on the grabbed teardrop - place it down
      placeTeardrop();
      return;
    }
  }

  // If holding a tile, check if clicking on it to place it down
  if (grabbedTile) {
    const intersectsGrabbed = raycaster.intersectObjects([grabbedTile], true);

    if (intersectsGrabbed.length > 0) {
      // Clicked on the grabbed tile - place it down
      placeTileInCircle();
      return;
    }
  }

  // Check for copper ore clicks first
  const copperIntersects = raycaster.intersectObjects(copperOres, true);

  if (copperIntersects.length > 0) {
    // Find the copper ore container
    let clickedCopper = copperIntersects[0].object;
    while (clickedCopper.parent && !copperOres.includes(clickedCopper)) {
      clickedCopper = clickedCopper.parent;
    }

    // Check if player is close enough to the copper ore
    if (copperOres.includes(clickedCopper) && isPlayerNearCopperOre(clickedCopper)) {
      activateCopperMode(clickedCopper);
      return;
    }
  }

  // Check for teardrop clicks
  const teardropIntersects = raycaster.intersectObjects(teardrops, true);

  if (teardropIntersects.length > 0) {
    // Find the teardrop container
    let clickedTeardrop = teardropIntersects[0].object;
    while (clickedTeardrop.parent && !teardrops.includes(clickedTeardrop)) {
      clickedTeardrop = clickedTeardrop.parent;
    }

    // Check if clicking on a teardrop (no distance check needed for general teardrop interaction)
    if (teardrops.includes(clickedTeardrop)) {
      // If clicking on the currently moving teardrop, stop moving it
      if (movingTeardrop === clickedTeardrop) {
        console.log('Stopped moving teardrop');
        movingTeardrop = null;
        return;
      }

      // Check if clicking on a central teardrop to activate Seed of Life (HIGHEST PRIORITY)
      if (isPlayerNearTeardrop(clickedTeardrop) && !seedOfLifeCreated) {
        const seedOfLifeData = checkSeedOfLifePattern(clickedTeardrop);
        if (seedOfLifeData) {
          activateSeedOfLife(seedOfLifeData);
          return;
        }
      }

      // If in copper mode and player is near, handle wire connection
      if (playerHasCopper && isPlayerNearTeardrop(clickedTeardrop)) {
        handleTeardropWireConnection(clickedTeardrop);
        return;
      }

      // If player is near and not in copper mode, grab the teardrop
      if (isPlayerNearTeardrop(clickedTeardrop) && !playerHasCopper) {
        grabTeardrop(clickedTeardrop);
        return;
      }

      // Otherwise, make this teardrop movable with arrow keys
      console.log('Started moving teardrop with arrow keys');
      movingTeardrop = clickedTeardrop;
      return;
    }
  }

  // Calculate objects intersecting the picking ray (tiles)
  const intersects = raycaster.intersectObjects(models, true);

  if (intersects.length > 0) {
    // Find the tile container (the clicked object might be a child of the container)
    let clickedTile = intersects[0].object;
    while (clickedTile.parent && !models.includes(clickedTile)) {
      clickedTile = clickedTile.parent;
    }

    // Check if player is in front of the tile and close enough
    if (models.includes(clickedTile) && isPlayerInFrontOfTile(clickedTile)) {
      grabTile(clickedTile);
    }
  }
});

// Function to check if player is in front of a tile
function isPlayerInFrontOfTile(tile) {
  const dx = player.position.x - tile.position.x;
  const dz = player.position.z - tile.position.z;
  const distance = Math.sqrt(dx * dx + dz * dz);

  // Check distance
  if (distance > grabDistance) {
    return false;
  }

  // Check if player is on the outer side of the tile (facing outward from center)
  const tileDistFromCenter = Math.sqrt(tile.position.x ** 2 + tile.position.z ** 2);
  const playerDistFromCenter = Math.sqrt(player.position.x ** 2 + player.position.z ** 2);

  // Player should be further from center than the tile
  return playerDistFromCenter > tileDistFromCenter;
}

// Function to check if player is near a teardrop
function isPlayerNearTeardrop(teardrop) {
  const dx = player.position.x - teardrop.position.x;
  const dz = player.position.z - teardrop.position.z;
  const distance = Math.sqrt(dx * dx + dz * dz);

  // Check distance (use same grab distance as tiles)
  return distance <= grabDistance;
}

// Function to grab a teardrop
function grabTeardrop(teardrop) {
  // Create a copy of the teardrop
  const teardropCopy = createTeardrop();
  teardropCopy.position.copy(teardrop.position);
  scene.add(teardropCopy);

  // Set as grabbed
  grabbedTeardrop = teardropCopy;

  console.log('Grabbed teardrop at', teardrop.position);
}

// Function to place the grabbed teardrop
function placeTeardrop() {
  if (!grabbedTeardrop) return;

  // The teardrop stays where it is when placed
  console.log('Placed teardrop at', grabbedTeardrop.position);

  // Add to teardrops array for future interaction
  teardrops.push(grabbedTeardrop);

  // Clear the grabbed reference
  grabbedTeardrop = null;
}

// Function to check if player is near a copper ore
function isPlayerNearCopperOre(copperOre) {
  const dx = player.position.x - copperOre.position.x;
  const dz = player.position.z - copperOre.position.z;
  const distance = Math.sqrt(dx * dx + dz * dz);

  // Check distance (use same grab distance as tiles)
  return distance <= grabDistance;
}

// Function to toggle copper mode when clicking on copper ore
function activateCopperMode(copperOre) {
  if (!isPlayerNearCopperOre(copperOre)) return;

  if (playerHasCopper) {
    // Turn off copper mode - revert to green
    playerHasCopper = false;
    player.material.color.setHex(originalPlayerColor);
    player.material.emissive.setHex(0x000000); // Remove emissive glow
    player.material.emissiveIntensity = 0;

    // Cancel any active wire drawing
    if (isDrawingWire) {
      if (currentWireLine) {
        scene.remove(currentWireLine);
      }
      if (currentWireMesh) {
        scene.remove(currentWireMesh);
      }
      isDrawingWire = false;
      currentWireLine = null;
      currentWireMesh = null;
      wireStartTeardrop = null;
    }

    console.log('Player deactivated copper mode! Returned to normal mode.');
  } else {
    // Activate copper mode
    playerHasCopper = true;
    player.material.color.setHex(copperColor);

    // Add emissive glow to match copper
    player.material.emissive.setHex(copperColor);
    player.material.emissiveIntensity = 0.3;

    console.log('Player activated copper mode! Can now draw copper wires.');
  }
}

// Function to handle teardrop wire connection
function handleTeardropWireConnection(teardrop) {
  if (!playerHasCopper) return;

  if (!isDrawingWire) {
    // Start drawing wire from this teardrop
    console.log('Started wire from teardrop at', teardrop.position);
    isDrawingWire = true;
    wireStartTeardrop = teardrop;

    // Create a new line starting at teardrop's position
    const points = [
      new THREE.Vector3(teardrop.position.x, teardrop.position.y, teardrop.position.z)
    ];

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color: copperColor,
      linewidth: 6
    });

    currentWireLine = new THREE.Line(geometry, material);
    scene.add(currentWireLine);

    wirePoints.push(points);
  } else {
    // Complete wire connection to this teardrop
    console.log('Completed wire to teardrop at', teardrop.position);

    // Remove old thin line
    scene.remove(currentWireLine);
    if (currentWireMesh) {
      scene.remove(currentWireMesh);
    }

    // Determine start point (either from teardrop or from current wire line)
    let startPoint;
    if (wireStartTeardrop) {
      // Wire started from a teardrop
      startPoint = new THREE.Vector3(
        wireStartTeardrop.position.x,
        wireStartTeardrop.position.y,
        wireStartTeardrop.position.z
      );
    } else if (currentWireLine) {
      // Wire started from player or somewhere else - use first point of current line
      const points = currentWireLine.geometry.attributes.position.array;
      startPoint = new THREE.Vector3(points[0], points[1], points[2]);
    } else {
      // Fallback - shouldn't happen
      console.error('No start point found for wire');
      isDrawingWire = false;
      currentWireLine = null;
      currentWireMesh = null;
      wireStartTeardrop = null;
      return;
    }

    const endPoint = new THREE.Vector3(
      teardrop.position.x,
      teardrop.position.y,
      teardrop.position.z
    );

    // Generate Hilbert curve path
    const hilbertPoints = generateHilbertPath(startPoint, endPoint, 4); // Order 4 Hilbert curve for more detail

    // Create thick 3D copper tube
    const hilbertLine = createThickLine(hilbertPoints, 0.08); // 3D tube with radius 0.08
    if (hilbertLine) {
      scene.add(hilbertLine);
      // Store the wire with metadata about which teardrops it connects
      hilbertLine.userData.startTeardrop = wireStartTeardrop;
      hilbertLine.userData.endTeardrop = teardrop;
      completedWires.push(hilbertLine); // Add to completed wires array for potential deletion
    }

    // Finish drawing - reset all state
    isDrawingWire = false;
    currentWireLine = null;
    currentWireMesh = null;
    wireStartTeardrop = null;

    console.log('Wire connection complete with Hilbert curve');
  }
}

// Function to toggle wire drawing on/off (for clicking on player sphere)
function toggleWireDrawing() {
  if (!playerHasCopper) return;

  if (isDrawingWire) {
    // Stop drawing - convert thin line to thick wire
    console.log('Wire drawing stopped');

    // Get all points from the current wire line
    if (currentWireLine) {
      const points = currentWireLine.geometry.attributes.position.array;
      const newPoints = [];

      // Copy existing points
      for (let i = 0; i < points.length; i += 3) {
        newPoints.push(new THREE.Vector3(points[i], points[i + 1], points[i + 2]));
      }

      // Remove old thin line
      scene.remove(currentWireLine);
      if (currentWireMesh) {
        scene.remove(currentWireMesh);
      }

      // Create Hilbert curve from start to end
      if (newPoints.length >= 2) {
        const startPoint = newPoints[0];
        const endPoint = newPoints[newPoints.length - 1];

        // Generate Hilbert curve path
        const hilbertPoints = generateHilbertPath(startPoint, endPoint, 4); // Order 4 for more detail

        // Create thick 3D copper tube
        const hilbertLine = createThickLine(hilbertPoints, 0.08); // 3D tube with radius 0.08
        if (hilbertLine) {
          scene.add(hilbertLine);
          completedWires.push(hilbertLine); // Add to completed wires array for potential deletion
        }
      }
    }

    isDrawingWire = false;
    currentWireLine = null;
    currentWireMesh = null;
    wireStartTeardrop = null;
  } else {
    // Start drawing - create new wire from player position
    console.log('Wire drawing started from player');
    isDrawingWire = true;
    wireStartTeardrop = null;

    // Create a new line starting at player's current position
    const points = [
      new THREE.Vector3(player.position.x, player.position.y, player.position.z)
    ];

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color: copperColor,
      linewidth: 6
    });

    currentWireLine = new THREE.Line(geometry, material);
    scene.add(currentWireLine);

    wirePoints.push(points);
  }
}

// Function to update wire line as player moves
function updateWireLine() {
  if (!currentWireLine || !playerHasCopper || !isDrawingWire) return;

  // Add current player position to the wire
  const points = currentWireLine.geometry.attributes.position.array;
  const newPoints = [];

  // Copy existing points
  for (let i = 0; i < points.length; i += 3) {
    newPoints.push(new THREE.Vector3(points[i], points[i + 1], points[i + 2]));
  }

  // Add new point at player's current position
  newPoints.push(new THREE.Vector3(player.position.x, player.position.y, player.position.z));

  // Update geometry
  currentWireLine.geometry.setFromPoints(newPoints);
  currentWireLine.geometry.attributes.position.needsUpdate = true;
}

// Function to grab a tile
function grabTile(tile) {
  grabbedTile = tile;
  // Remove from models array
  const index = models.indexOf(tile);
  if (index > -1) {
    models.splice(index, 1);
  }

  // Rearrange remaining tiles to close the gap
  closeGap();
}

// Function to place the grabbed tile back into the circle
function placeTileInCircle() {
  if (!grabbedTile) return;

  const tilePos = grabbedTile.position;
  const distFromCenter = Math.sqrt(tilePos.x ** 2 + tilePos.z ** 2);

  // Determine which ring the tile should go into based on distance from center
  let targetRadius;

  if (Math.abs(distFromCenter - outerRadius) < Math.abs(distFromCenter - middleRadius)) {
    if (Math.abs(distFromCenter - outerRadius) < Math.abs(distFromCenter - innerRadius)) {
      targetRadius = outerRadius;
    } else {
      targetRadius = innerRadius;
    }
  } else {
    if (Math.abs(distFromCenter - middleRadius) < Math.abs(distFromCenter - innerRadius)) {
      targetRadius = middleRadius;
    } else {
      targetRadius = innerRadius;
    }
  }

  // Find tiles in the target ring
  const ringTiles = models.filter(tile => {
    const tileDist = Math.sqrt(tile.position.x ** 2 + tile.position.z ** 2);
    return Math.abs(tileDist - targetRadius) < 5;
  });

  // Calculate angle of the grabbed tile
  const tileAngle = Math.atan2(tilePos.z, tilePos.x);

  // Find the two nearest tiles to determine insertion position
  let insertIndex = 0;
  if (ringTiles.length > 0) {
    const tilesWithAngles = ringTiles.map(tile => ({
      tile: tile,
      angle: Math.atan2(tile.position.z, tile.position.x)
    }));

    // Sort by angle
    tilesWithAngles.sort((a, b) => a.angle - b.angle);

    // Find where to insert based on angle
    for (let i = 0; i < tilesWithAngles.length; i++) {
      if (tileAngle < tilesWithAngles[i].angle) {
        insertIndex = models.indexOf(tilesWithAngles[i].tile);
        break;
      }
    }

    // If we didn't find a spot, add to the end
    if (insertIndex === 0 && tilesWithAngles.length > 0) {
      insertIndex = models.indexOf(tilesWithAngles[tilesWithAngles.length - 1].tile) + 1;
    }
  }

  // Insert the tile back into models array
  models.splice(insertIndex, 0, grabbedTile);

  // Snap the tile to the correct radius immediately
  const tileAngleFinal = Math.atan2(grabbedTile.position.z, grabbedTile.position.x);
  grabbedTile.position.x = Math.cos(tileAngleFinal) * targetRadius;
  grabbedTile.position.z = Math.sin(tileAngleFinal) * targetRadius;
  grabbedTile.position.y = 0;

  // Make it face outward
  const lookAtX = grabbedTile.position.x * 2;
  const lookAtZ = grabbedTile.position.z * 2;
  grabbedTile.lookAt(lookAtX, 0, lookAtZ);

  // Store the placed tile reference before clearing it
  const placedTile = grabbedTile;

  // Clear the grabbed tile reference
  grabbedTile = null;

  // Check for doors BEFORE rearranging (so we can find the pair we just created)
  console.log('Tile placed, checking for doors...');
  checkForNewlyCreatedPair(placedTile, targetRadius);

  // Rearrange all tiles in the circle (without animation delay)
  rearrangeAllRings();

  // Update the counter display
  updateCounter(false);
}

// Function to check if placing a tile created a new matching pair
function checkForNewlyCreatedPair(placedTile, targetRadius) {
  // Check if doors are already open for this specific ring
  if (targetRadius === outerRadius && outerDoorsOpen) return;
  if (targetRadius === middleRadius && middleDoorsOpen) return;
  if (targetRadius === innerRadius && innerDoorsOpen) return;

  // Get all tiles in the same ring as the placed tile
  const ringTiles = models.filter(tile => {
    const tileDist = Math.sqrt(tile.position.x ** 2 + tile.position.z ** 2);
    return Math.abs(tileDist - targetRadius) < 5;
  });

  // Sort by angle
  const tilesWithAngles = ringTiles.map(tile => ({
    tile: tile,
    angle: Math.atan2(tile.position.z, tile.position.x)
  }));
  tilesWithAngles.sort((a, b) => a.angle - b.angle);

  // Find the placed tile in the sorted array
  const placedIndex = tilesWithAngles.findIndex(t => t.tile === placedTile);
  if (placedIndex === -1) return;

  // Check the tiles immediately before and after the placed tile
  const prevIndex = (placedIndex - 1 + tilesWithAngles.length) % tilesWithAngles.length;
  const nextIndex = (placedIndex + 1) % tilesWithAngles.length;

  const placedBaseName = getTileBaseName(getTileFilename(placedTile));
  const prevBaseName = getTileBaseName(getTileFilename(tilesWithAngles[prevIndex].tile));
  const nextBaseName = getTileBaseName(getTileFilename(tilesWithAngles[nextIndex].tile));

  console.log('Placed:', placedBaseName, 'Prev:', prevBaseName, 'Next:', nextBaseName, 'Ring:', targetRadius);

  // Check if the placed tile matches either neighbor (works for all three rings)
  if (placedBaseName === prevBaseName && placedBaseName && (targetRadius === outerRadius || targetRadius === middleRadius || targetRadius === innerRadius)) {
    console.log('Found matching pair with previous tile! Opening doors...');
    // alert('Opening doors for: ' + placedBaseName); // Removed popup
    openDoors(placedTile, tilesWithAngles[prevIndex].tile, targetRadius);
  } else if (placedBaseName === nextBaseName && placedBaseName && (targetRadius === outerRadius || targetRadius === middleRadius || targetRadius === innerRadius)) {
    console.log('Found matching pair with next tile! Opening doors...');
    // alert('Opening doors for: ' + placedBaseName); // Removed popup
    openDoors(placedTile, tilesWithAngles[nextIndex].tile, targetRadius);
  }
}

// Function to close the gap after a tile is removed
function closeGap() {
  rearrangeAllRings();
}

// Function to rearrange all rings
function rearrangeAllRings() {
  // Group tiles by their radius (distance from center)
  const rings = [
    { tiles: [], radius: outerRadius, files: outerRingTileFiles },
    { tiles: [], radius: middleRadius, files: middleRingTileFiles },
    { tiles: [], radius: innerRadius, files: innerRingTileFiles }
  ];

  // Categorize remaining tiles by which ring they're in
  models.forEach(tile => {
    const distFromCenter = Math.sqrt(tile.position.x ** 2 + tile.position.z ** 2);

    if (Math.abs(distFromCenter - outerRadius) < 5) {
      rings[0].tiles.push(tile);
    } else if (Math.abs(distFromCenter - middleRadius) < 5) {
      rings[1].tiles.push(tile);
    } else if (Math.abs(distFromCenter - innerRadius) < 5) {
      rings[2].tiles.push(tile);
    }
  });

  // Rearrange each ring's tiles
  rings.forEach(ring => {
    // Check if this ring has doors open
    const hasDoorsOpen = (ring.radius === outerRadius && outerDoorsOpen && outerDoorGapAngle !== null) ||
                         (ring.radius === middleRadius && middleDoorsOpen && middleDoorGapAngle !== null) ||
                         (ring.radius === innerRadius && innerDoorsOpen && innerDoorGapAngle !== null);

    if (hasDoorsOpen) {
      // Determine which door gap angle to use
      let doorGapAngle;
      if (ring.radius === outerRadius) {
        doorGapAngle = outerDoorGapAngle;
      } else if (ring.radius === middleRadius) {
        doorGapAngle = middleDoorGapAngle;
      } else if (ring.radius === innerRadius) {
        doorGapAngle = innerDoorGapAngle;
      }

      // Keep the gap at the door position
      const totalTiles = ring.tiles.length;
      const gapCount = 2; // Space for 2 tiles (the door pair)
      const totalPositions = totalTiles + gapCount;

      ring.tiles.forEach((tile, index) => {
        // Calculate angle accounting for the gap
        const angleStep = (Math.PI * 2) / totalPositions;

        // Offset angles to avoid the door gap area
        // We need to find where the gap should be in our sequence
        const gapPositionInSequence = Math.floor((doorGapAngle / (Math.PI * 2)) * totalPositions);

        // Adjust index to skip the gap positions
        let adjustedIndex = index;
        if (index >= gapPositionInSequence) {
          adjustedIndex += gapCount;
        }

        const angle = adjustedIndex * angleStep;
        const targetX = Math.cos(angle) * ring.radius;
        const targetZ = Math.sin(angle) * ring.radius;

        // Animate to new position
        animateTileToPosition(tile, targetX, targetZ);
      });
    } else {
      // Normal rearrangement for rings without doors open
      ring.tiles.forEach((tile, index) => {
        const angle = (index / ring.tiles.length) * Math.PI * 2;
        const targetX = Math.cos(angle) * ring.radius;
        const targetZ = Math.sin(angle) * ring.radius;

        // Animate to new position
        animateTileToPosition(tile, targetX, targetZ);
      });
    }
  });
}

// Function to smoothly animate tile to new position
function animateTileToPosition(tile, targetX, targetZ) {
  const duration = 200; // milliseconds - reduced from 500 for faster response
  const startX = tile.position.x;
  const startZ = tile.position.z;
  const startTime = Date.now();

  // Debug: Log tile animation for door tiles
  const targetDist = Math.sqrt(targetX ** 2 + targetZ ** 2);
  const startDist = Math.sqrt(startX ** 2 + startZ ** 2);
  console.log(`Animating tile ${tile.userData.filename}: from (${startX.toFixed(2)}, ${startZ.toFixed(2)}) r=${startDist.toFixed(2)} to (${targetX.toFixed(2)}, ${targetZ.toFixed(2)}) r=${targetDist.toFixed(2)}`);

  // Sanity check: Don't animate tiles to positions way outside the rings
  if (targetDist > 100 || isNaN(targetX) || isNaN(targetZ)) {
    console.error(`ERROR: Invalid target position for tile ${tile.userData.filename}! Aborting animation.`);
    return;
  }

  // Create a unique animation ID for this animation
  const animationId = Date.now() + Math.random();

  // Cancel any previous animation on this tile
  if (tile.userData.currentAnimationId) {
    tile.userData.animationCancelled = true;
  }

  // Store this animation ID on the tile
  tile.userData.currentAnimationId = animationId;
  tile.userData.animationCancelled = false;

  function animate() {
    // Check if this animation was cancelled
    if (tile.userData.animationCancelled && tile.userData.currentAnimationId !== animationId) {
      return; // Stop this animation
    }

    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Ease-out function
    const easeProgress = 1 - Math.pow(1 - progress, 3);

    tile.position.x = startX + (targetX - startX) * easeProgress;
    tile.position.z = startZ + (targetZ - startZ) * easeProgress;

    // Update rotation to face outward
    const lookAtX = tile.position.x * 2;
    const lookAtZ = tile.position.z * 2;
    tile.lookAt(lookAtX, 0, lookAtZ);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      // Animation complete
      if (tile.userData.currentAnimationId === animationId) {
        tile.userData.currentAnimationId = null;
      }
    }
  }

  animate();
}

// Collision detection function
function checkCollision(newX, newZ, ignoreGrabbedTile = false) {
  const playerRadius = 0.5; // Sphere radius
  const tileWidth = 2; // Approximate tile width (adjust based on actual tile size)

  for (let i = 0; i < models.length; i++) {
    const tile = models[i];

    // Skip the grabbed tile if specified
    if (ignoreGrabbedTile && tile === grabbedTile) {
      continue;
    }

    // Skip tiles that we're temporarily ignoring for collision
    if (ignoreTilesForCollision.has(tile)) {
      continue;
    }

    const dx = newX - tile.position.x;
    const dz = newZ - tile.position.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Check if player would collide with this tile
    if (distance < (playerRadius + tileWidth)) {
      return true; // Collision detected
    }
  }
  return false; // No collision
}

// Teardrop collision detection function
function checkTeardropCollision(newX, newZ) {
  const playerRadius = 0.5; // Player sphere radius
  const teardropRadius = 0.3; // Teardrop size
  const collisionDistance = playerRadius + teardropRadius + 0.2; // Add buffer for smooth collision

  for (const teardrop of teardrops) {
    // Skip the grabbed teardrop (player can move through it)
    if (teardrop.userData.grabbed) {
      continue;
    }

    const dx = newX - teardrop.position.x;
    const dz = newZ - teardrop.position.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Check if player would collide with this teardrop
    if (distance < collisionDistance) {
      return true; // Collision detected
    }
  }
  return false; // No collision
}

// Copper ore collision detection function
function checkCopperOreCollision(newX, newZ) {
  const playerRadius = 0.5; // Player sphere radius
  const copperOreRadius = 0.5; // Copper ore approximate size (scaled to 0.25)
  const collisionDistance = playerRadius + copperOreRadius + 0.2; // Add buffer for smooth collision

  for (const copperOre of copperOres) {
    const dx = newX - copperOre.position.x;
    const dz = newZ - copperOre.position.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Check if player would collide with this copper ore
    if (distance < collisionDistance) {
      return true; // Collision detected
    }
  }
  return false; // No collision
}

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();
let lastElapsedTime = 0;

renderer.setAnimationLoop(() => {
  const elapsedTime = clock.getElapsedTime();
  const deltaTime = elapsedTime - lastElapsedTime;
  lastElapsedTime = elapsedTime;

  // Check if we're moving a teardrop or the player
  if (movingTeardrop) {
    // Move the teardrop instead of the player
    const teardropSpeed = playerSpeed; // Use same speed as player

    // Get current angle and radius of the teardrop
    let currentAngle = Math.atan2(movingTeardrop.position.z, movingTeardrop.position.x);
    let currentRadius = Math.sqrt(movingTeardrop.position.x ** 2 + movingTeardrop.position.z ** 2);

    let newAngle = currentAngle;
    let newRadius = currentRadius;

    if (keys.left) {
      newAngle += teardropSpeed / currentRadius;
    }
    if (keys.right) {
      newAngle -= teardropSpeed / currentRadius;
    }
    if (keys.up) {
      newRadius -= teardropSpeed;
    }
    if (keys.down) {
      newRadius += teardropSpeed;
    }

    // Calculate new position for teardrop
    movingTeardrop.position.x = Math.cos(newAngle) * newRadius;
    movingTeardrop.position.z = Math.sin(newAngle) * newRadius;
    // Keep y position constant
  } else {
    // Update player movement with collision detection
    // Get current angle and radius
    let currentAngle = Math.atan2(player.position.z, player.position.x);
    let currentRadius = Math.sqrt(player.position.x ** 2 + player.position.z ** 2);

    let newAngle = currentAngle;
    let newRadius = currentRadius;

    if (keys.left) {
      // Move counterclockwise around the circle
      newAngle += playerSpeed / currentRadius;
    }
    if (keys.right) {
      // Move clockwise around the circle
      newAngle -= playerSpeed / currentRadius;
    }
    if (keys.up) {
      // Move inward (toward center)
      newRadius -= playerSpeed;
    }
    if (keys.down) {
      // Move outward (away from center)
      newRadius += playerSpeed;
    }

    // Calculate new position
    const newX = Math.cos(newAngle) * newRadius;
    const newZ = Math.sin(newAngle) * newRadius;

    // Check collision - player shouldn't collide with other tiles (excluding grabbed tile), teardrops, or copper ore
    const collidesWithTile = checkCollision(newX, newZ);
    const collidesWithTeardrop = checkTeardropCollision(newX, newZ);
    const collidesWithCopperOre = checkCopperOreCollision(newX, newZ);

    // Only update position if no collision
    if (!collidesWithTile && !collidesWithTeardrop && !collidesWithCopperOre) {
      player.position.x = newX;
      player.position.z = newZ;

      // Check if player has crossed through a door and is safely past
      const currentPlayerRadius = Math.sqrt(player.position.x ** 2 + player.position.z ** 2);

      // If outer doors are open and player is now well inside the outer ring (past it by at least 5 units)
      if (outerDoorsOpen && currentPlayerRadius < outerRadius - 5) {
        console.log('Player safely past outer door!');
        closeDoors(outerRadius);

        // Update text when player passes outer ring for the first time
        if (!playerPassedOuterRing) {
          playerPassedOuterRing = true;
          updateTextWithFade(
            `now, you are past the first ring. there's more to be done before we reach the ultimate circuitry! move to the teardrop transistor and use those to create a NOT gate. click on the teardrop transistor to create more teardrop transistors, and arrange them in the shape of a NOT gate. then move to the copper ore to gather copper to draw copper wires to connect the teardrop transistors. once you've completed this, find another two matching tiles for the middle ring to advance. wishing you transistance without resistance.`
          );
        }
      }

      // If middle doors are open and player is now well inside the middle ring (past it by at least 5 units)
      if (middleDoorsOpen && currentPlayerRadius < middleRadius - 5) {
        console.log('Player safely past middle door!');
        closeDoors(middleRadius);

        // Update text when player passes middle ring for the first time
        if (!playerPassedMiddleRing) {
          playerPassedMiddleRing = true;
          updateTextWithFade(
            `you have made it past the second ring. now, it's time to create an AND gate. once you do this, you can move into the innermost ring, and you shall have access to create the most sacred circuit, the circuit that is the answer to it all!`
          );
        }
      }

      // If inner doors are open and player is now well inside the inner ring (past it by at least 5 units)
      if (innerDoorsOpen && currentPlayerRadius < innerRadius - 5) {
        console.log('Player safely past inner door!');
        closeDoors(innerRadius);

        // Update text when player passes inner ring for the first time
        if (!playerPassedInnerRing) {
          playerPassedInnerRing = true;
          updateTextWithFade(
            `you've almost reached the pinnacle. now, arrange 6 teardrop transistors in a hexagonal shape around a central teardrop transistor. click on the central teardrop transistor and you will unlock the sacred.`
          );
        }
      }

      // Update last radius for next frame
      playerLastRadius = currentPlayerRadius;
    }
  }

  // Update wire line if currently drawing (every frame, not just when moving)
  if (isDrawingWire && currentWireLine && playerHasCopper) {
    updateWireLine();
  }

  // If holding a teardrop, make it follow the player
  if (grabbedTeardrop) {
    // Position teardrop slightly in front of the player (toward center)
    const directionX = -player.position.x;
    const directionZ = -player.position.z;
    const length = Math.sqrt(directionX * directionX + directionZ * directionZ);
    const normalizedX = directionX / length;
    const normalizedZ = directionZ / length;

    grabbedTeardrop.position.x = player.position.x + normalizedX * 1.5;
    grabbedTeardrop.position.z = player.position.z + normalizedZ * 1.5;
    grabbedTeardrop.position.y = player.position.y;
  }

  // If holding a tile, make it follow the player
  if (grabbedTile) {
    // Position tile slightly in front of the player (toward center)
    const directionX = -player.position.x;
    const directionZ = -player.position.z;
    const length = Math.sqrt(directionX * directionX + directionZ * directionZ);
    const normalizedX = directionX / length;
    const normalizedZ = directionZ / length;

    grabbedTile.position.x = player.position.x + normalizedX * 1.5;
    grabbedTile.position.z = player.position.z + normalizedZ * 1.5;
    grabbedTile.position.y = player.position.y;

    // Make tile face the same direction as it would in the ring
    const lookAtX = grabbedTile.position.x * 2;
    const lookAtZ = grabbedTile.position.z * 2;
    grabbedTile.lookAt(lookAtX, 0, lookAtZ);
  }

  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);
});
